import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

import { ReCaptchaV3Service } from 'ng-recaptcha';
import { finalize, mergeMap, switchMap } from 'rxjs/operators';
import { of, Subject, Subscription, throwError, timer } from 'rxjs';

import { UserTokens } from 'src/app/interfaces';
import { ModalService } from '../modal/modal.service';
import { environment } from 'src/environments/environment';
import { HEADER_DONT_SAVE, HEADER_DONT_SAVE_REQ } from '@app/constants';
import { SingularService } from 'src/infra/analytics/singular/singular.service';
import { BiometricService } from '../biometric/biometric.service';
import { Utils } from '../utils/utils.service';
@Injectable({
   providedIn: 'root'
})
export class AuthService {

   private readonly JWT_TOKEN = 'JWT_TOKEN';
   private readonly REFRESH_TOKEN = 'REFRESH_TOKEN';
   private readonly ID_TOKEN = 'ID_TOKEN';
   private readonly EXP_AUTH = 'EXP_AUTH';
   private readonly UNE_FORM =  'UNE_FORM';
   private readonly AUTO_LOGOUT = 'ALOO';
   private readonly GPS_LOCATION = 'GPS_LOCATION';

   public readonly REFERENCE_USER = 'RFU';
   public readonly ANALYTIC_CODE = 'ACM';
   public readonly FROM_LANDING = 'FLM';
   public readonly INTERNAL_REFERRAL = 'INTRFF';

   cleanCacheInterceptor = new Subject<boolean>();

   referred: string;
   registerPromo: string;

   /** Tiempo para expiración de sesión */
   expirationTime = { warning: 0, expire: 0 };
   expirationWarnings = { warning: false, expire: false };

   private sessionTimer: Subscription;

   constructor(
      private readonly http: HttpClient,
      private readonly router: Router,
      private readonly recaptchaV3Serv: ReCaptchaV3Service,
      private readonly modalServ: ModalService,
      private readonly singularServ: SingularService,
      private readonly biometriServ: BiometricService,
      private readonly utilsServ: Utils,
   ) { }

   /** Definir token CSRF */
   init() {
      this.http.get(`${environment.apiUrlBase}/init`).subscribe();
   }

   initSessionTimer() {
      if (!this.sessionTimer && this.tokenIsActive() && environment.production) {
         // REVISIÓN DE TIEMPO DE SESIÓN CADA 5 SEGUNDOS
         this.sessionTimer = timer(0, 5000).subscribe(() => this.checkExpirationTime());
      }
   }

   validateReCaptcha(action: string) {
      return this.recaptchaV3Serv.execute(action);
   }

   registry(email: string, password: string, userType: 'u' | 'd', tms?: boolean, mailProm?: boolean, referred?: string, promo?: string, phone?: string,
      campaignFragment = '') {
      const e = email.trim().toLowerCase();
      const p = `${e}|${password}`;
      const t = this.utilsServ.utf8ToBase64(userType);
      const prm = mailProm;
      const ph = phone.trim();
      const cf = campaignFragment?.trim()?.toLowerCase();


      return this.validateReCaptcha('signUp').pipe(
         switchMap(cr => {
            return this.http.post<UserTokens>(`${environment.apiUrlBase}/auth/registry`,
               { ctd: this.utilsServ.utf8ToBase64(p), cr, r: referred || this.referred, pr: promo || this.registerPromo, t, tms, prm, ph, cf }, HEADER_DONT_SAVE).pipe(
                  switchMap(res => {
                     if (res.a) {
                        this.storeTokens(res);
                        this.initSessionTimer();
                        return of(res);
                     }

                     return throwError(() => new Error('Error'));
                  })
               )
         }));
   }

   /** Fn | Actualiza observable con hora para expiración */
   updateExpirationTime() {
      const warning = new Date();
      const expire = new Date();
      warning.setSeconds(warning.getSeconds() + 240);
      expire.setSeconds(expire.getSeconds() + 300);
      this.expirationTime.warning = warning.getTime();
      this.expirationTime.expire = expire.getTime();
   }

   checkExpirationTime() {
      const now = Date.now();

      // ALERTA
      if (!this.expirationWarnings.warning && now > this.expirationTime.warning) {
         this.expirationWarnings.warning = true;
         const modalRef = this.modalServ.modalCustom({
            img: 'https://static.monific.com/assets/assets-app/img/icon/img/attention.svg',
            title: 'Advertencia',
            message:
               'Parece que has estado inactivo por un rato. Tu sesión se cerrará automáticamente en 1 minuto si no hay actividad.',
            buttonText: 'Estoy aquí',
         });
         modalRef.beforeClosed().subscribe((r: any) => {
            this.expirationWarnings.warning = false;
            this.updateExpirationTime();
         });
         // CERRAR SESIÓN
      } else if (now > this.expirationTime.expire && this.expirationWarnings.warning) {
         sessionStorage.setItem('SIN', '1')
         sessionStorage.setItem(this.AUTO_LOGOUT, '1');
         this.logout();
      }
   }

   getCognitoUser() {
      return this.http.get<{ cognitoUser: string }>(`${environment.apiUrlBase}/auth/get-c-user`, HEADER_DONT_SAVE);
   }

   login(email: string, pw: string) {
      this.cleanSessionTokens();

      const mail = email.trim().toLowerCase();
      const sd = `${mail}|${pw}`;

      const params = { ctd: this.utilsServ.utf8ToBase64(sd) };

      return this.http.post<UserTokens>(`${environment.apiUrlBase}/auth/login`, params, HEADER_DONT_SAVE).pipe(
         mergeMap((res) => {
            if (res.a) {
               this.storeTokens(res);
               return of(res);
            }
            return throwError(() => Error());
         }),
      );
   }

   /** Función para verificar si el correo electronico existe en la base de datos */
   verifyExistEmail(email: string) {

      const mail = email.trim().toLowerCase();
      const sd = `${mail}`;

      const params = { ctd: this.utilsServ.utf8ToBase64(sd) };

      return this.http.post<any>(`${environment.apiUrlBase}/auth/verify-email`, params, HEADER_DONT_SAVE);

   }

   private storeTokens(tokens: UserTokens) {
      sessionStorage.setItem(this.JWT_TOKEN, tokens.a);
      sessionStorage.setItem(this.REFRESH_TOKEN, tokens.r);
      sessionStorage.setItem(this.ID_TOKEN, tokens.i);
      sessionStorage.setItem(this.EXP_AUTH, String(Date.now() + 4800000))
      sessionStorage.setItem(this.AUTO_LOGOUT, '0');
   }

   tokenIsActive() {
      const exp = sessionStorage.getItem(this.EXP_AUTH);

      return exp ? Number(exp) > Date.now() : false;
   }

   changePassword(actual: string, newPass: string) {
      const ac = this.utilsServ.utf8ToBase64(actual);
      const ne = this.utilsServ.utf8ToBase64(newPass);

      return this.http.post(`${environment.apiUrlBase}/auth/change-password`, { ac, ne }, HEADER_DONT_SAVE);
   }

   sendRecoverEmail(email: { email: string }) {
      return this.http.post(`${environment.apiUrlBase}/auth/forget-password-email`, email, HEADER_DONT_SAVE);
   }

   validateToken(context: { c: string; m: string; }) {
      return this.http.post(`${environment.apiUrlBase}/auth/confirm-code`, context, HEADER_DONT_SAVE);
   }

   resetUserPassword(c: string, m: string, newPass: string) {
      return this.http.post(`${environment.apiUrlBase}/auth/reset-password`, { c, m, n: this.utilsServ.utf8ToBase64(newPass) }, HEADER_DONT_SAVE);
   }

   logout() {
      this.biometriServ.saveLogout('true');
      this.singularServ.logout();
      this.http.post(`${environment.apiUrlBase}/auth/logout`, {}, HEADER_DONT_SAVE).pipe(finalize(() => this.cleanSession())).subscribe();
   }

   cleanSessionTokens() {
      sessionStorage.removeItem(this.JWT_TOKEN);
      sessionStorage.removeItem(this.REFRESH_TOKEN);
      sessionStorage.removeItem(this.ID_TOKEN);
      sessionStorage.removeItem(this.EXP_AUTH);
      sessionStorage.removeItem(this.UNE_FORM);
      sessionStorage.removeItem(this.GPS_LOCATION);
   }

   cleanSession() {
      this.cleanSessionTokens();
      this.cleanCacheInterceptor.next(true);
      this.router.navigate(['/']).then(() => window.location.reload());
   }

   getUserInfoFromUpdateRequest(token: string) {
      const headers = HEADER_DONT_SAVE_REQ.set('tk', token);
      return this.http.post<{ n: any, u: any }>(`${environment.apiUrlBase}/user/pd/req-update`, {}, { headers });
   }

   /**
   * Función para obtener la información del usuario con OTP de formulario de alto riesgo
   * @return n: any, u: User
   */
   getUserInfoFromFormPLD(token: string) {
      const headers = HEADER_DONT_SAVE_REQ.set('tk', token);
      return this.http.post<{ p: any, u: any }>(`${environment.apiUrlBase}/user/pd/req-update-pld-warning`, {}, { headers });
   }

   /** Función para validar si el token es valido para actualizar datos */
   isTokenValidUpdateInfo(token: string) {
      const headers = HEADER_DONT_SAVE_REQ.set('tk', token);
      return this.http.post<any>(`${environment.apiUrlBase}/user/update-info/validate-token`, {}, { headers });
   }

   setStatusBiometric(email: string, status: boolean) {
      return this.http.post(`${environment.apiUrlBase}/auth/set-biometric`, { email, status });
   }

   getReferralId(): string | null {
      const storedData = localStorage.getItem(this.INTERNAL_REFERRAL);
      if (!storedData) return null;

      try {
        const parsed = JSON.parse(storedData);
        const now = Date.now();

        if (parsed?.id && parsed?.expiresAt && now < parsed.expiresAt) {
          return parsed.id;
        }
      } catch (e) {
        return null;
      }

      return null;
    }

}
